import { useState } from "react";
import {
  Card,
  RECORD_RAIL_WIDTH,
  ResponsiveSplit,
} from "@/design-system/components/ui";
import { Button } from "@/design-system/components/ui";
import { Table, THead, TBody, TR, TH, TD } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import { useSpvPoolMath } from "@/hooks/capitalMarkets/useSpvPools";
import { SPV_POOL_STATUSES, type SpvPoolDto } from "@/types/spvPool";
import {
  formatCurrency,
  formatDate,
  formatPct,
  humanizeStatus,
} from "../../shared/format";
import { AdvanceStatusDialog } from "../components/AdvanceStatusDialog";

interface OverviewTabProps {
  pool: SpvPoolDto;
  onOpenTab: (tab: "composition" | "idup" | "certifications") => void;
}

export function OverviewTab({ pool, onOpenTab }: OverviewTabProps) {
  const { hasPermission } = useAuth();
  const mathQuery = useSpvPoolMath(pool.id);
  const canAdvance = hasPermission("SpvPool.AdvanceStatus");
  const [advanceOpen, setAdvanceOpen] = useState(false);

  const idx = SPV_POOL_STATUSES.indexOf(pool.status);
  const nextStatus = idx >= 0 ? SPV_POOL_STATUSES[idx + 1] : undefined;

  const math = mathQuery.data;

  return (
    // Container-driven — Table & Tile Standard §12. This was
    // `lg:grid-cols-3` + `lg:col-span-2`, which asks the WINDOW how wide
    // this box is; the window cannot answer that once the box is a tab
    // body, a drawer or a split pane.
    <ResponsiveSplit
      gap="md"
      mainMinWidth="34rem"
      railWidth={RECORD_RAIL_WIDTH}
      railGrows={false}
      main={
        <div className="flex flex-col gap-space-4">
          <Card>
            <h3 className="font-display font-bold text-h3 m-0 mb-space-3">
              Pool Summary
            </h3>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-space-3 m-0">
              <Field label="Offering Type" value={pool.offeringType || "—"} />
              <Field label="Status" value={humanizeStatus(pool.status)} />
              <Field
                label="Inception Date"
                value={formatDate(pool.inceptionDate)}
              />
              <Field label="Closing Date" value={formatDate(pool.closingDate)} />
              <Field
                label="Target Pool Size"
                value={formatCurrency(pool.targetPoolSize)}
              />
              <Field
                label="Min Segment Count"
                value={String(pool.minSegmentCount)}
              />
              <Field
                label="Max Borrower Concentration"
                value={formatPct(pool.maxBorrowerConcentrationPct)}
              />
              <Field
                label="Wtd Avg Credit Rating Target"
                value={pool.weightedAvgCreditRatingTarget || "—"}
              />
            </dl>
            {pool.notes && (
              <p className="text-small text-gray-mid mt-space-3 mb-0 whitespace-pre-line">
                {pool.notes}
              </p>
            )}
          </Card>

          <Card>
            <h3 className="font-display font-bold text-h3 m-0 mb-space-3">
              Tranches
            </h3>
            {math && math.perTrancheFill.length > 0 ? (
              <Table>
                <THead>
                  <TR>
                    <TH>Tranche</TH>
                    <TH>Committed</TH>
                    <TH>Target</TH>
                    <TH>Fill %</TH>
                  </TR>
                </THead>
                <TBody>
                  {math.perTrancheFill.map((t) => (
                    <TR key={t.poolTrancheId}>
                      <TD>{t.trancheCode}</TD>
                      <TD>{formatCurrency(t.committedAmount)}</TD>
                      <TD>{formatCurrency(t.targetAmount)}</TD>
                      <TD>{formatPct(t.fillPct)}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            ) : (
              <p className="text-small text-gray-mid italic m-0">
                No tranches configured yet.
              </p>
            )}
          </Card>

          <Card>
            <h3 className="font-display font-bold text-h3 m-0 mb-space-3">
              Composition Stats
            </h3>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-space-3 m-0">
              <Field
                label="Contributed Segments"
                value={math ? String(math.contributedSegmentCount) : "—"}
              />
              <Field
                label="Eligible Segment Total"
                value={math ? String(math.eligibleSegmentTotal) : "—"}
              />
              <Field
                label="Contribution % (Segments)"
                value={formatPct(math?.contributionPctSegments)}
              />
              <Field
                label="Contribution % (Principal)"
                value={formatPct(math?.contributionPctPrincipal)}
              />
              <Field
                label="Contributed Principal"
                value={formatCurrency(math?.contributedPrincipal)}
              />
              <Field
                label="Funded Fill %"
                value={formatPct(math?.fundedFillPct)}
              />
            </dl>
          </Card>
      </div>
      }
      rail={
        <div className="grid gap-space-4 min-w-0">

        <div className="flex flex-col gap-space-4">
          <Card>
            <h3 className="font-display font-bold text-h3 m-0 mb-space-3">
              Service Providers
            </h3>
            <p className="text-small text-gray-mid m-0">
              Service provider assignments are managed on the SPV trust. Open the
              trust to review the indenture trustee, trust company, placement
              agent and backup servicer.
            </p>
          </Card>

          <Card>
            <h3 className="font-display font-bold text-h3 m-0 mb-space-3">
              Quick Actions
            </h3>
            <div className="flex flex-col gap-space-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => onOpenTab("composition")}
              >
                Open Composition
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => onOpenTab("idup")}
              >
                Generate IDUP
              </Button>
              <span title="Coming in PR-5">
                <Button variant="secondary" size="sm" disabled className="w-full">
                  Invite Investor
                </Button>
              </span>
              {canAdvance && nextStatus && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setAdvanceOpen(true)}
                  data-testid="overview-advance-status"
                >
                  Advance status
                </Button>
              )}
            </div>
          </Card>
        </div>

        {nextStatus && (
          <AdvanceStatusDialog
            open={advanceOpen}
            onClose={() => setAdvanceOpen(false)}
            pool={pool}
            targetStatus={nextStatus}
            onOpenCertification={() => {
              setAdvanceOpen(false);
              onOpenTab("certifications");
            }}
          />
        )}
    </div>
      }
    />
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <dt className="text-label font-display uppercase tracking-label text-gray-mid">
        {label}
      </dt>
      <dd className="text-small text-onyx m-0">{value}</dd>
    </div>
  );
}
